import { IconButton } from "@mui/material";
import { AddButton } from "app/components/Button/AddButton";
import { FilterSelect } from "app/components/FilterSelect";
import { ThreedotIcon } from "app/icons";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  selectFrontClearanceManufacturerFilter,
  selectFrontClearanceManufacturerOptions,
  selectFrontClearanceMasterModelFilter,
  selectFrontClearanceSuperModelOptions,
} from "../redux/selector";
import { frontClearanceActions } from "../redux/slice";
import { FrontClearanceFilterOption, FrontClearanceFilterValue } from "../types";
import { useStyles } from "./styles";

type Props = {
  onAddClick?: () => void;
};

const ALL_VALUE: FrontClearanceFilterValue = "ALL";

const buildFilterOptions = (options: FrontClearanceFilterOption[] = []) => [
  ALL_VALUE,
  ...(Array.isArray(options) ? options : []).map((option) =>
    String(option.id),
  ),
];

const getOptionLabel = (
  value: FrontClearanceFilterValue,
  options: FrontClearanceFilterOption[] = [],
  allLabel: string,
) => {
  if (value === ALL_VALUE) {
    return allLabel;
  }

  const safeOptions = Array.isArray(options) ? options : [];

  return (
    safeOptions.find((option) => String(option.id) === value)?.name ?? value
  );
};

const ActionBar = ({ onAddClick }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();

  const manufacturerFilter = useSelector(
    selectFrontClearanceManufacturerFilter,
  );
  const masterModelFilter = useSelector(selectFrontClearanceMasterModelFilter);

  const manufacturerOptions = useSelector(
    selectFrontClearanceManufacturerOptions,
  );
  const superModelOptions = useSelector(selectFrontClearanceSuperModelOptions);

  const manufacturerSelectOptions = useMemo(
    () => buildFilterOptions(manufacturerOptions),
    [manufacturerOptions],
  );
  const masterModelSelectOptions = useMemo(
    () => buildFilterOptions(superModelOptions),
    [superModelOptions],
  );

  const allLabel = t("FRONT_CLEARANCE.FILTERS.ALL");

  return (
    <div className={styles.root} dir="ltr">
      <div className={styles.actions}>
        <AddButton
          label={t("FRONT_CLEARANCE.ACTIONS.ADD")}
          onClick={onAddClick}
        />
        <IconButton size="small" className={styles.iconButton}>
          <ThreedotIcon />
        </IconButton>
      </div>

      <div className={styles.filters}>
        <FilterSelect
          label={t("FRONT_CLEARANCE.FILTERS.MASTER_MODEL.LABEL")}
          value={masterModelFilter}
          options={masterModelSelectOptions}
          getOptionLabel={(value) =>
            getOptionLabel(value, superModelOptions, allLabel)
          }
          onChange={(value) => {
            dispatch(frontClearanceActions.setMasterModelFilter(value));
          }}
        />
        <FilterSelect
          label={t("FRONT_CLEARANCE.FILTERS.MANUFACTURER.LABEL")}
          value={manufacturerFilter}
          options={manufacturerSelectOptions}
          getOptionLabel={(value) =>
            getOptionLabel(value, manufacturerOptions, allLabel)
          }
          onChange={(value) => {
            dispatch(frontClearanceActions.setManufacturerFilter(value));
          }}
        />
      </div>
    </div>
  );
};

export default ActionBar;
